import { ref, shallowRef, watch } from 'vue'
import { RoomEvent, Track as LKTrack, VideoPresets } from 'livekit-client'

import type { Ref, ShallowRef } from 'vue'
import type {
  LocalTrackPublication,
  Participant,
  RemoteTrack,
  RemoteTrackPublication,
  Room,
  Track,
  TrackPublication,
} from 'livekit-client'

import { refRoom } from '@hooks/classroom/room-live-kit.hook'

interface TracksLiveKitReturn {
  localCameraTrack: ShallowRef<Track | null>
  remoteCameraTrack: ShallowRef<Track | null>
  remoteAudioTrack: ShallowRef<Track | null>
  isCameraEnabled: Ref<boolean>
  isMicEnabled: Ref<boolean>
  isRemoteCameraMuted: Ref<boolean>
  isRemoteMicMuted: Ref<boolean>
  isLocalSpeaking: Ref<boolean>
  isRemoteSpeaking: Ref<boolean>
  cameraError: Ref<string | null>
  toggleCamera: () => Promise<void>
  toggleMic: () => Promise<void>
  remoteScreenTrack: ShallowRef<Track | null>
  remoteScreenAudioTrack: ShallowRef<Track | null>
  isLocalScreenSharing: Ref<boolean>
  toggleScreenShare: () => Promise<void>
}

const localCameraTrack: ShallowRef<Track | null> = shallowRef(null)
export const remoteCameraTrack: ShallowRef<Track | null> = shallowRef(null)
export const remoteAudioTrack: ShallowRef<Track | null> = shallowRef(null)
const remoteScreenTrack: ShallowRef<Track | null> = shallowRef(null)
const remoteScreenAudioTrack: ShallowRef<Track | null> = shallowRef(null)
const isCameraEnabled = ref<boolean>(false)
const isMicEnabled = ref<boolean>(false)
export const isRemoteCameraMuted = ref<boolean>(false)
export const isRemoteMicMuted = ref<boolean>(false)
const isLocalSpeaking = ref<boolean>(false)
const isRemoteSpeaking = ref<boolean>(false)
const isLocalScreenSharing = ref<boolean>(false)
const cameraError = ref<string | null>(null)

const isCamera = (publication: TrackPublication): boolean => publication.source === LKTrack.Source.Camera
const isMic = (publication: TrackPublication): boolean => publication.source === LKTrack.Source.Microphone
const isScreen = (publication: TrackPublication): boolean => publication.source === LKTrack.Source.ScreenShare
const isScreenAudio = (publication: TrackPublication): boolean => publication.source === LKTrack.Source.ScreenShareAudio

const applyMuteChange = (publication: TrackPublication, participant: Participant, isMuted: boolean): void => {
  if (isCamera(publication)) {
    if (participant.isLocal) isCameraEnabled.value = !isMuted
    else if (publication.videoTrack === remoteCameraTrack.value) isRemoteCameraMuted.value = isMuted
  } else if (isMic(publication)) {
    if (participant.isLocal) isMicEnabled.value = !isMuted
    else if (publication.audioTrack === remoteAudioTrack.value) isRemoteMicMuted.value = isMuted
  }
}

const clearTracks = (): void => {
  localCameraTrack.value = null
  remoteCameraTrack.value = null
  remoteAudioTrack.value = null
  remoteScreenTrack.value = null
  remoteScreenAudioTrack.value = null
  isCameraEnabled.value = false
  isMicEnabled.value = false
  isRemoteCameraMuted.value = false
  isRemoteMicMuted.value = false
  isLocalSpeaking.value = false
  isRemoteSpeaking.value = false
  isLocalScreenSharing.value = false
  cameraError.value = null
}

const syncExisting = (room: Room): void => {
  const local = room.localParticipant
  localCameraTrack.value = local.getTrackPublication(LKTrack.Source.Camera)?.videoTrack ?? null
  isCameraEnabled.value = local.isCameraEnabled
  isMicEnabled.value = local.isMicrophoneEnabled
  isLocalSpeaking.value = local.isSpeaking
  isLocalScreenSharing.value = local.isScreenShareEnabled

  for (const participant of room.remoteParticipants.values()) {
    const cameraPub = participant.getTrackPublication(LKTrack.Source.Camera)
    if (cameraPub?.videoTrack) {
      remoteCameraTrack.value = cameraPub.videoTrack
      isRemoteCameraMuted.value = cameraPub.isMuted
    }
    const micPub = participant.getTrackPublication(LKTrack.Source.Microphone)
    if (micPub?.audioTrack) {
      remoteAudioTrack.value = micPub.audioTrack
      isRemoteMicMuted.value = micPub.isMuted
    }
    const screenPub = participant.getTrackPublication(LKTrack.Source.ScreenShare)
    if (screenPub?.videoTrack) {
      remoteScreenTrack.value = screenPub.videoTrack
    }
    const screenAudioPub = participant.getTrackPublication(LKTrack.Source.ScreenShareAudio)
    if (screenAudioPub?.audioTrack) {
      remoteScreenAudioTrack.value = screenAudioPub.audioTrack
    }
    if (participant.isSpeaking) {
      isRemoteSpeaking.value = true
    }
  }
}

const bindTrackEvents = (room: Room): void => {
  room
    .on(RoomEvent.LocalTrackPublished, (publication: LocalTrackPublication) => {
      if (isCamera(publication)) {
        localCameraTrack.value = publication.videoTrack ?? null
        isCameraEnabled.value = true
      } else if (isMic(publication)) {
        isMicEnabled.value = true
      } else if (isScreen(publication)) {
        isLocalScreenSharing.value = true
      }
    })
    .on(RoomEvent.LocalTrackUnpublished, (publication: LocalTrackPublication) => {
      if (isCamera(publication)) {
        localCameraTrack.value = null
        isCameraEnabled.value = false
      } else if (isMic(publication)) {
        isMicEnabled.value = false
      } else if (isScreen(publication)) {
        isLocalScreenSharing.value = false
      }
    })
    .on(RoomEvent.TrackSubscribed, (track: RemoteTrack, publication: RemoteTrackPublication) => {
      if (isCamera(publication)) {
        remoteCameraTrack.value = track
        isRemoteCameraMuted.value = publication.isMuted
      } else if (isMic(publication)) {
        remoteAudioTrack.value = track
        isRemoteMicMuted.value = publication.isMuted
      } else if (isScreen(publication)) {
        remoteScreenTrack.value = track
      } else if (isScreenAudio(publication)) {
        remoteScreenAudioTrack.value = track
      }
    })
    .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, publication: RemoteTrackPublication) => {
      if (isCamera(publication) && remoteCameraTrack.value === track) {
        remoteCameraTrack.value = null
      } else if (isMic(publication) && remoteAudioTrack.value === track) {
        remoteAudioTrack.value = null
      } else if (isScreen(publication) && remoteScreenTrack.value === track) {
        remoteScreenTrack.value = null
      } else if (isScreenAudio(publication) && remoteScreenAudioTrack.value === track) {
        remoteScreenAudioTrack.value = null
      }
    })
    .on(RoomEvent.TrackMuted, (publication: TrackPublication, participant: Participant) => {
      applyMuteChange(publication, participant, true)
    })
    .on(RoomEvent.TrackUnmuted, (publication: TrackPublication, participant: Participant) => {
      applyMuteChange(publication, participant, false)
    })
    .on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
      isLocalSpeaking.value = speakers.some((participant) => participant.isLocal)
      isRemoteSpeaking.value = speakers.some((participant) => !participant.isLocal)
    })
}

watch(refRoom, (room) => {
  if (room) {
    clearTracks()
    bindTrackEvents(room)
    syncExisting(room)
  } else {
    clearTracks()
  }
}, { immediate: true })

export const useTracksLiveKit = (): TracksLiveKitReturn => {
  const toggleCamera = async (): Promise<void> => {
    const room = refRoom.value
    if (!room) return

    cameraError.value = null
    try {
      await room.localParticipant.setCameraEnabled(!room.localParticipant.isCameraEnabled)
    } catch (error) {
      cameraError.value = error instanceof Error ? error.name : 'CAMERA_ERROR'
      console.error('[classroom] camera toggle failed', error)
    }
  }

  const toggleMic = async (): Promise<void> => {
    const room = refRoom.value
    if (!room) return

    try {
      await room.localParticipant.setMicrophoneEnabled(!room.localParticipant.isMicrophoneEnabled)
    } catch (error) {
      console.error('[classroom] mic toggle failed', error)
    }
  }

  const toggleScreenShare = async (): Promise<void> => {
    const room = refRoom.value
    if (!room) return

    try {
      await room.localParticipant.setScreenShareEnabled(
        !room.localParticipant.isScreenShareEnabled,
        { resolution: VideoPresets.h1080.resolution, audio: true },
      )
    } catch (error) {
      if (error instanceof Error && error.name === 'NotAllowedError') return
      console.error('[classroom] screen share toggle failed', error)
    }
  }

  return {
    localCameraTrack,
    remoteCameraTrack,
    remoteAudioTrack,
    isCameraEnabled,
    isMicEnabled,
    isRemoteCameraMuted,
    isRemoteMicMuted,
    isLocalSpeaking,
    isRemoteSpeaking,
    cameraError,
    toggleCamera,
    toggleMic,
    remoteScreenTrack,
    remoteScreenAudioTrack,
    isLocalScreenSharing,
    toggleScreenShare,
  }
}
